import { useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import {
  getBoreholeSVG, getBulkMeterSVG, getColoredMeterSVG, getControlValveSVG,
  getIncidentSVG, getOfftakeSVG, getTankSVG, getTreatmentWorksSVG, getWeirSVG,
} from "../../assets/icons/legendicons/mlegendicons";
import { SHOW_ALL_KEY } from "../../components/gis/attributeAnalyzer";
import { humanizeFieldName } from "./fieldLabels";

const svgGenerators = {
  "Customer Meters": getColoredMeterSVG,
  "Tanks": getTankSVG,
  "ControlValves": getControlValveSVG,
  "Boreholes": getBoreholeSVG,
  "Weirs": getWeirSVG,
  "T-Works": getTreatmentWorksSVG,
  "Offtakes": getOfftakeSVG,
  "Bulkmeters": getBulkMeterSVG,
  "Incidents": getIncidentSVG,
};

const LINE_LAYER_NAMES = ["Wards", "DMAs", "SupplyLines", "Billing Routes"];

const Legend = ({
  layers, setLayers,
  layerSchemas, layerClassifications,
  onAttributeChange, onToggleClass,
  myData,
  legendCollapsed, setLegendCollapsed,
  basemapsVisible, setBasemapsVisible,
  basemapSelected, setBasemapSelected,
}) => {
  const toggleLayerVisibility = (layerName) => {
    setLayers((prev) => ({
      ...prev,
      [layerName]: { ...prev[layerName], visibility: !prev[layerName].visibility },
    }));
  };

  return (
    <div className="legend">
      <div className="legend-top">
        <h3>Legend</h3>
        <FaTimes onClick={() => setLegendCollapsed(!legendCollapsed)} />
      </div>
      <div className="legend-scroll">
        {Object.entries(layers).reverse().map(([layerName, layerConfig]) => (
          <LegendLayer
            key={layerName}
            layerName={layerName}
            layerConfig={layerConfig}
            schema={layerSchemas?.[layerName] || []}
            classes={layerClassifications?.[layerName] || []}
            iconGenerator={svgGenerators[layerName]}
            onToggleVisibility={() => toggleLayerVisibility(layerName)}
            onAttributeChange={onAttributeChange}
            onToggleClass={onToggleClass}
          />
        ))}

        <div className="legend-layer">
          <div className="layer-toggle">
            <input
              type="checkbox"
              checked={basemapsVisible}
              onChange={() => setBasemapsVisible(!basemapsVisible)}
            />
            <label>Basemaps</label>
          </div>
          <div className="attribute-filters">
            {basemapsVisible &&
              myData.map((item, index) => (
                <Basemap
                  key={index}
                  index={index}
                  label={item.name}
                  basemapSelected={basemapSelected}
                  setBasemapSelected={setBasemapSelected}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function LegendLayer({
  layerName, layerConfig, schema, classes, iconGenerator,
  onToggleVisibility, onAttributeChange, onToggleClass,
}) {
  const activeAttribute = layerConfig.activeAttribute;
  const classFilters = layerConfig.classFilters?.[activeAttribute] || {};
  const isClassVisible = (key) => (classFilters[key] !== undefined ? classFilters[key] : true);
  const hasData = layerConfig.data && layerConfig.data.length > 0;
  const featureCount = layerConfig.data?.length || 0;

  // Per-class counts — how many features actually fall into each bucket,
  // so "Active (1,204)" beats a checkbox with no idea what it's hiding.
  const classCounts = useMemo(() => {
    if (!activeAttribute || activeAttribute === SHOW_ALL_KEY || !layerConfig.data) return {};
    const counts = {};
    for (const cls of classes) counts[cls.key] = 0;
    for (const item of layerConfig.data) {
      const match = classes.find((c) => c.test(item[activeAttribute]));
      if (match) counts[match.key] = (counts[match.key] || 0) + 1;
    }
    return counts;
  }, [layerConfig.data, activeAttribute, classes]);

  return (
    <div className="legend-layer">
      <div className="layer-toggle">
        <input type="checkbox" checked={layerConfig.visibility} onChange={onToggleVisibility} />
        <label>{layerName}</label>
        {hasData && <span className="layer-count">{featureCount.toLocaleString()}</span>}
      </div>

      {/* Gated on visibility only — toggling the layer off collapses both
          the "symbolize by" selector and every class checkbox. */}
      {layerConfig.visibility && hasData && (
        <div className="attribute-filters">
          <select
            className="attribute-select"
            value={activeAttribute || SHOW_ALL_KEY}
            onChange={(e) => onAttributeChange(layerName, e.target.value)}
          >
            <option value={SHOW_ALL_KEY}>Show All (single color)</option>
            {schema.map((s) => (
              <option key={s.field} value={s.field}>
                By: {humanizeFieldName(s.field)}
              </option>
            ))}
          </select>

          {classes.map((cls) => {
            const isVisible = isClassVisible(cls.key);
            const count = classCounts[cls.key];
            return (
              <div key={cls.key} className={`attribute-item${isVisible ? "" : " attribute-item--off"}`}>
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={() => onToggleClass(layerName, activeAttribute, cls.key)}
                />
                {iconGenerator ? (
                  <img
                    src={iconGenerator(cls.color)}
                    alt={`${cls.label} icon`}
                    className="legend-icon"
                  />
                ) : (
                  <span
                    className={
                      LINE_LAYER_NAMES.includes(layerName)
                        ? "line-color-indicator"
                        : "circle-color-indicator"
                    }
                    style={{ backgroundColor: cls.color }}
                  ></span>
                )}
                <label>{cls.label}</label>
                {count !== undefined && <span className="class-count">{count.toLocaleString()}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Basemap = ({ index, label, basemapSelected, setBasemapSelected }) => (
  <div className="attribute-item">
    <input type="radio" onChange={() => setBasemapSelected(index)} checked={basemapSelected === index} />
    <label>{label}</label>
  </div>
);

export default Legend;
